import { getStaffMember } from "@/features/staff/services/staffService";
import { getUserById } from "@/features/users/services/users";
import type { User } from "@/features/users/schemas/user";
import type { SubjectType } from "@/features/rbac/schemas/rbac";

/**
 * Identidad de la persona a la que se le aplican permisos, desacoplada de dónde
 * vive su perfil.
 *
 * ESTE ARCHIVO ES LA ÚNICA COSTURA entre el RBAC y el origen de la identidad.
 * Cuando las cuentas del equipo se movieron de `users` a `staff`, este fue el
 * único archivo del slice que cambió: todo lo demás consume `RbacSubject` y
 * nunca `User`.
 *
 * Resuelve primero `staff` y solo cae a `users` cuando la persona todavía no
 * tiene perfil de equipo, que es lo que mantiene vivo el camino de arranque
 * mientras se migran las cuentas.
 */
export interface RbacSubject {
  readonly uid: string;
  readonly email: string | null;
  readonly displayName: string;
  readonly source: SubjectType;
  /** `users.type`. Solo se usa para el fallback de compatibilidad. */
  readonly legacyType: string | null;
  /** `users.permission_admin`. Solo se usa para el fallback de compatibilidad. */
  readonly legacyPermissions: readonly string[];
  /** `users.vm_admin`. Gate histórico de entrada a la aplicación. */
  readonly legacyVmAdmin: boolean;
}

function displayNameOf(user: User): string {
  const composed = [user.forename, user.surname].filter(Boolean).join(" ").trim();
  return user.name?.trim() || composed || user.email || user.uid;
}

function fromUser(uid: string, user: User): RbacSubject {
  return {
    uid,
    email: user.email ?? null,
    displayName: displayNameOf(user),
    source: "user",
    legacyType: user.type ?? null,
    legacyPermissions: Array.isArray(user.permission_admin) ? user.permission_admin : [],
    legacyVmAdmin: user.vm_admin === true,
  };
}

/**
 * Resuelve la identidad de un uid de Firebase Auth.
 *
 * Devuelve `null` si no existe perfil en ninguna de las dos colecciones, lo que
 * el resolver de permisos trata como conjunto vacío: sin perfil no hay permisos.
 */
export async function resolveSubject(uid: string): Promise<RbacSubject | null> {
  if (!uid) return null;

  const staffMember = await getStaffMember(uid);
  if (staffMember) {
    return {
      uid,
      email: staffMember.email || null,
      displayName: staffMember.name || staffMember.email || uid,
      source: "staff",
      // Una cuenta de equipo no arrastra nada del modelo de la plataforma
      // médica: sus permisos salen exclusivamente de sus roles asignados.
      legacyType: null,
      legacyPermissions: [],
      legacyVmAdmin: false,
    };
  }

  const user = await getUserById(uid);
  if (!user) return null;

  return fromUser(uid, user);
}

/** Resuelve varias identidades a la vez, descartando las que no existen. */
export async function resolveSubjects(uids: readonly string[]): Promise<RbacSubject[]> {
  const unique = [...new Set(uids.filter(Boolean))];
  const resolved = await Promise.all(unique.map((uid) => resolveSubject(uid)));
  return resolved.filter((subject): subject is RbacSubject => subject !== null);
}
